import React, { useMemo, useEffect, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import BackHeader from '../components/BackHeader';
import RestaurantItem from '../components/RestaurantItem';
import LoadingState from '../components/LoadingState';
import ErrorState from '../components/ErrorState';
import EmptyState from '../components/EmptyState';
import NavigationBar from '../components/NavigationBar';
import BottomBanner from '../components/BottomBanner';
import { useRestaurants } from '../hooks/useRestaurants';
import { getCategories } from '../api/categoryService';
import { getStatusMessages } from '../config/constants';
import { Category } from '../types/category';
import { getCurrentLanguage } from '../config/languageConfig';
import useTranslation from '../hooks/useTranslation';
import { SupportedCurrency, getCurrencyFromLanguage } from '../utils/currencyUtils';
import { Restaurant } from '../types/restaurant';

const CategoryDetail: React.FC = () => {
  const { categoryId, categoryName, chainName, chainId } = useParams<{ 
    categoryId?: string, 
    categoryName?: string,
    chainId?: string,
    chainName?: string
  }>();
  const location = useLocation();
  const [allCategories, setAllCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const { t } = useTranslation();
  const [currentLang, setCurrentLang] = useState(getCurrentLanguage());
  const [selectedCurrency, setSelectedCurrency] = useState<SupportedCurrency>(
    getCurrencyFromLanguage(currentLang)
  );
  
  // 언어 변경 감지를 위한 이벤트 리스너 추가
  useEffect(() => {
    const handleLanguageChange = () => {
      const newLang = getCurrentLanguage();
      setCurrentLang(newLang);
      setSelectedCurrency(getCurrencyFromLanguage(newLang));
    };
    
    // 언어 변경 이벤트 리스너 등록
    window.addEventListener('language-changed', handleLanguageChange);
    
    // 컴포넌트 언마운트 시 이벤트 리스너 제거
    return () => {
      window.removeEventListener('language-changed', handleLanguageChange);
    };
  }, []);
  
  // 다국어 상태 메시지 가져오기
  const STATUS_MESSAGES = getStatusMessages();
  
  // API에서 카테고리 데이터 가져오기
  useEffect(() => {
    const fetchCategoriesData = async () => {
      try {
        const categories = await getCategories();
        if (categories.length > 0) {
          setAllCategories(categories);
          
          // categoryId로 현재 카테고리 찾기
          if (categoryId) {
            const category = categories.find(cat => cat.id === categoryId);
            if (category) {
              setSelectedCategory(category);
            }
          }
        }
      } catch (error) {
        console.error('카테고리 데이터를 가져오는데 실패했습니다:', error);
      }
    };
    
    fetchCategoriesData();
  }, [categoryId]);
  
  // 현재 어떤 타입의 요청인지 확인
  const isAllRestaurants = location.pathname === '/restaurants' || location.pathname === '/browse';
  const isPopularChain = !!chainId && !!chainName;
  
  // 검색 조건 구성
  const searchParams = useMemo(() => {
    if (isAllRestaurants) {
      return { allRestaurants: true };
    }
    
    if (chainId && chainName) {
      return { franchiseId: chainId };
    }
    
    // 카테고리 ID가 있는 경우 - 가장 우선시
    if (categoryId) {
      return { categoryId };
    }
    
    // 선택된 카테고리가 있는 경우 - 차선
    if (selectedCategory) {
      return { categoryId: selectedCategory.id };
    }
    
    // 하위 호환성을 위해 카테고리 이름이 있는 경우
    if (categoryName) {
      // 이름으로 카테고리 ID를 찾아서 사용
      const category = allCategories.find(cat => 
        cat.nameKo === decodeURIComponent(categoryName) || cat.name === decodeURIComponent(categoryName)
      );
      
      if (category) {
        return { categoryId: category.id };
      }
      
      // 찾지 못한 경우 기존 방식 사용
      return { categoryName };
    }
    
    return {};
  }, [categoryId, categoryName, chainId, chainName, isAllRestaurants, selectedCategory, allCategories]);
  
  // API 요청
  const { restaurants, loading, error, refetch } = useRestaurants(searchParams);
  
  // franchiseId가 같은 가게 중 첫번째만 필터링 (카테고리 보기일 때만)
  const filteredRestaurants = useMemo(() => {
    // Popular Chains나 모든 레스토랑 보기인 경우 필터링 없이 그대로 반환
    if (isPopularChain || isAllRestaurants) {
      return restaurants;
    }
    
    // 카테고리별 보기인 경우에는 franchiseId 기준으로 필터링
    const franchiseMap = new Map<string, Restaurant>();
    
    restaurants.forEach(restaurant => {
      // franchiseId가 있는 경우 중복 검사
      if (restaurant.franchiseId) {
        // 해당 franchiseId의 가게가 아직 없으면 추가
        if (!franchiseMap.has(restaurant.franchiseId)) {
          franchiseMap.set(restaurant.franchiseId, restaurant);
        }
      } else {
        // franchiseId가 없는 가게는 id를 키로 추가 (모든 가게가 표시됨)
        franchiseMap.set(restaurant.id, restaurant);
      }
    });
    
    // Map의 값들만 배열로 반환
    return Array.from(franchiseMap.values());
  }, [restaurants, isPopularChain, isAllRestaurants]);
  
  // 카테고리 언어별 이름 가져오기
  const getCategoryLocalizedName = (category: Category): string => {
    if (!category) return '';
    
    // 현재 언어에 맞는 필드 찾기
    switch (currentLang) {
      case 'ko':
        return category.nameKo || category.nameEn || category.name;
      case 'ja':
        return category.nameJa || category.nameEn || category.name;
      case 'zh-CN':
        return category.nameZhCn || category.nameEn || category.name;
      case 'zh-TW':
        return category.nameZhTw || category.nameEn || category.name;
      case 'en':
      default:
        return category.nameEn || category.name;
    }
  };
  
  // 표시할 타이틀 결정
  const displayTitle = useMemo((): string => {
    if (isAllRestaurants) {
      return t('browse.allRestaurants');
    }
    
    if (chainName) {
      // 체인점은 그대로 표시 (영문명 이미 설정되어 있음)
      return decodeURIComponent(chainName);
    }
    
    // 선택된 카테고리가 있으면 그 이름을 표시
    if (selectedCategory) {
      return getCategoryLocalizedName(selectedCategory);
    }
    
    // ID로 카테고리 찾기
    if (categoryId) {
      const category = allCategories.find(cat => cat.id === categoryId);
      if (category) {
        return getCategoryLocalizedName(category);
      }
    }
    
    // 카테고리명으로 찾기 (하위 호환성)
    if (categoryName) {
      const decodedName = decodeURIComponent(categoryName);
      const category = allCategories.find(cat => 
        cat.nameKo === decodedName || cat.name === decodedName
      );
      if (category) {
        return getCategoryLocalizedName(category);
      }
      return decodedName;
    }
    
    return t('browse.restaurants');
  }, [categoryId, categoryName, chainName, isAllRestaurants, allCategories, selectedCategory, currentLang, t]);
  
  return (
    <div className="min-h-screen bg-gray-50">
      <BackHeader title={displayTitle} />
      
      <main className="p-4 pb-20">
        {/* 콘텐츠 영역: 로딩/에러/빈 결과/목록 중 하나만 표시 */}
        {loading ? (
          <LoadingState 
            message={STATUS_MESSAGES.loading.restaurants} 
            id={`category-${isAllRestaurants ? 'all-restaurants' : categoryId || 'unknown'}`}
          />
        ) : error ? (
          <ErrorState 
            message={t('status.error.restaurants')} 
            onRetry={refetch} 
            subtitle={t('error.connectionIssue')}
          />
        ) : filteredRestaurants.length === 0 ? (
          <EmptyState 
            message={STATUS_MESSAGES.empty.restaurants} 
            subtitle={t('empty.changeLocationOrCategory')}
            actionText={t('action.refresh')}
            onAction={refetch}
            hideIcon={true}
          />
        ) : (
          <div>
            {filteredRestaurants.map((restaurant) => (
              <RestaurantItem 
                key={restaurant.id} 
                restaurant={restaurant} 
                currency={selectedCurrency}
                isFromCategory={!isPopularChain && !isAllRestaurants}
              />
            ))}
          </div>
        )}
      </main>
      
      {/* 네비게이션 바 추가 */}
      <BottomBanner />
      <NavigationBar />
    </div>
  );
};

export default CategoryDetail; 